import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  Stack,
  Typography,
  Avatar,
  IconButton,
  MenuItem,
  Card,
} from "@mui/material";
import { FormControlLabel, Switch } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import DragIndicator from "@mui/icons-material/DragIndicator";
import Div from "@jumbo/shared/Div";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { CROSS_SELL_PLACEMENTS } from "app/utils/constants/settings";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const SortableItem = ({
  id,
  item,
  index,
  currencyCode,
  onRemove,
  i18n,
  defaultLanguage,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const lang = i18n?.language || defaultLanguage || "en";
  const localizedName =
    (item?.name &&
      (item.name[lang] || item.name[defaultLanguage] || item.name.en)) ||
    item?.name ||
    "";
  return (
    <Card ref={setNodeRef} style={style} sx={{ p: 1 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <IconButton
          size="small"
          sx={{ cursor: "grab" }}
          {...attributes}
          {...listeners}
        >
          <DragIndicator fontSize="small" />
        </IconButton>
        <Avatar
          variant="rounded"
          src={item?.imageUrl || ""}
          sx={{ width: 48, height: 48 }}
        >
          {String(localizedName || "?")
            .charAt(0)
            .toUpperCase()}
        </Avatar>
        <Stack flex={1} mr={2}>
          <Typography variant="subtitle1" noWrap>
            {localizedName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {item?.productId || ""}
          </Typography>
        </Stack>
        <Typography variant="subtitle2">
          {Number(item?.price ?? 0).toFixed(2)} {currencyCode || ""}
        </Typography>
        <IconButton color="error" size="small" onClick={onRemove}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Card>
  );
};

const CrossSellTab = ({
  t,
  values,
  setFieldValue,
  i18n,
  defaultLanguage,
  currencyCode,
  setCrossSellPickerOpen,
}) => {
  const placementsOptions = CROSS_SELL_PLACEMENTS;
  return (
    <Box px={1} py={1}>
      {values?.crossSell ? (
        <>
          <Stack direction="row" spacing={2} mb={2} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={!!values.crossSell.enabled}
                  onChange={(e) =>
                    setFieldValue("crossSell.enabled", e.target.checked)
                  }
                />
              }
              label={t("pages.crossSell.enabled")}
            />
            <Div sx={{ minWidth: 160 }}>
              <JumboTextField
                type="number"
                size="small"
                name="crossSell.max"
                label={t("pages.crossSell.maxRecommendations")}
                value={values.crossSell.max || 0}
                InputProps={{
                  inputProps: { min: 0 },
                  onChange: (e) =>
                    setFieldValue(
                      "crossSell.max",
                      Math.max(0, Number(e.target.value || 0))
                    ),
                }}
              />
            </Div>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>{t("pages.crossSell.placements")}</InputLabel>
              <Select
                multiple
                value={values.crossSell.placements || []}
                onChange={(e) =>
                  setFieldValue("crossSell.placements", e.target.value)
                }
                input={
                  <OutlinedInput label={t("pages.crossSell.placements")} />
                }
                renderValue={(selected) => (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {(selected || []).map((val) => (
                      <Chip
                        key={val}
                        label={t(
                          `pages.dsokCategories.crossSellPlacements.${val}`
                        )}
                        size="small"
                      />
                    ))}
                  </Stack>
                )}
              >
                {placementsOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {t(`pages.dsokCategories.crossSellPlacements.${opt}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCrossSellPickerOpen(true)}
            >
              {t("buttons.addProduct")}
            </Button>
          </Stack>
          <Divider />
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={({ active, over }) => {
              if (!over || active.id === over.id) return;
              const current = values.crossSell.items || [];
              const oldIndex = current.findIndex(
                (it, idx) => `${it.id || it.productId}-${idx}` === active.id
              );
              const newIndex = current.findIndex(
                (it, idx) => `${it.id || it.productId}-${idx}` === over.id
              );
              if (oldIndex < 0 || newIndex < 0) return;
              const reordered = arrayMove(current, oldIndex, newIndex).map(
                (it, idx) => ({ ...it, order: idx })
              );
              setFieldValue("crossSell.items", reordered);
            }}
          >
            <SortableContext
              items={(values.crossSell.items || []).map(
                (it, idx) => `${it.id || it.productId}-${idx}`
              )}
              strategy={verticalListSortingStrategy}
            >
              <Stack spacing={1} mt={2}>
                {(values.crossSell.items || []).map((item, index) => {
                  const id = `${item.id || item.productId}-${index}`;
                  return (
                    <SortableItem
                      key={id}
                      id={id}
                      item={item}
                      index={index}
                      currencyCode={currencyCode}
                      onRemove={() => {
                        const items = (values.crossSell.items || []).filter(
                          (it, idx) => idx !== index
                        );
                        setFieldValue("crossSell.items", items);
                      }}
                      i18n={i18n}
                      defaultLanguage={defaultLanguage}
                    />
                  );
                })}
              </Stack>
            </SortableContext>
          </DndContext>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t("pages.crossSell.noData")}
        </Typography>
      )}
    </Box>
  );
};

export default CrossSellTab;
